import type { ReportDefinition, ReportColumnDef, ReportFilterField } from '../types';

function statusFilter(options: { value: string; label: string }[], searchPlaceholder = 'Number, name…'): ReportFilterField[] {
  return [
    { key: 'dateRange', label: 'Date range', type: 'dateRange' },
    { key: 'search', label: 'Search', type: 'text', placeholder: searchPlaceholder },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [{ value: '', label: 'All' }, ...options],
    },
  ];
}

const dateStatusFilters = statusFilter([
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Cancelled', label: 'Cancelled' },
]);

/** Supplier invoices / bills — includes Posted (live AP status). */
const supplierInvoiceStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Posted', label: 'Posted' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'Invoice #, supplier…'
);

/** Purchase returns — Draft → Posted lifecycle. */
const purchaseReturnStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Posted', label: 'Posted' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'Return #, supplier…'
);

/** Service receipts — Draft → Posted lifecycle. */
const serviceReceiptStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Posted', label: 'Posted' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'SR #, PO, supplier…'
);

const salesInvoiceStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Posted', label: 'Posted' },
    { value: 'Sent', label: 'Sent' },
    { value: 'Partially Paid', label: 'Partially Paid' },
    { value: 'Paid', label: 'Paid' },
    { value: 'Overdue', label: 'Overdue' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'Invoice #, customer…'
);

const salesOrderStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Confirmed', label: 'Confirmed' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'SO #, customer…'
);

const salesDnStatusFilters = statusFilter(
  [
    { value: 'Prepared', label: 'Prepared' },
    { value: 'In Transit', label: 'In Transit' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Returned', label: 'Returned' },
  ],
  'DN #, customer, SO…'
);

const poStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Sent to Supplier', label: 'Sent to Supplier' },
    { value: 'Acknowledged', label: 'Acknowledged' },
    { value: 'Partially Received', label: 'Partially Received' },
    { value: 'Fully Received', label: 'Fully Received' },
    { value: 'Closed', label: 'Closed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'PO #, supplier…'
);

const grnStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Posted', label: 'Posted' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'GRN #, supplier, PO…'
);

const prStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'Partially Ordered', label: 'Partially Ordered' },
    { value: 'Fully Ordered', label: 'Fully Ordered' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'PR #, requester…'
);

const stockMovementFilters: ReportFilterField[] = [
  { key: 'dateRange', label: 'Date range', type: 'dateRange' },
  { key: 'search', label: 'Search', type: 'text', placeholder: 'Item, voucher…' },
  {
    key: 'status',
    label: 'Type',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      // Coarse groups — backend expands to live transaction_type values
      { value: 'Receipt', label: 'Receipt' },
      { value: 'Issue', label: 'Issue' },
      { value: 'Transfer', label: 'Transfer' },
      { value: 'Adjustment', label: 'Adjustment' },
      { value: 'Return', label: 'Return' },
      { value: 'Opening', label: 'Opening' },
    ],
  },
];

const mrStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'Partially Issued', label: 'Partially Issued' },
    { value: 'Fully Issued', label: 'Fully Issued' },
    { value: 'Closed', label: 'Closed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'MR #…'
);

const miTypeFilters = statusFilter(
  [
    { value: 'Full', label: 'Full' },
    { value: 'Partial', label: 'Partial' },
  ],
  'MI #, WO…'
);

const woStatusFilters = statusFilter(
  [
    { value: 'Draft', label: 'Draft' },
    { value: 'Released', label: 'Released' },
    { value: 'IIR Pending', label: 'Job Inspection Pending' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Stage In Progress', label: 'Stage In Progress' },
    { value: 'Inspection Pending', label: 'Inspection Pending' },
    { value: 'FIR Pending', label: 'FIR Pending' },
    { value: 'Ready for Dispatch', label: 'Ready for Dispatch' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Closed', label: 'Closed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  'WO #, item, customer…'
);

const woRegisterFilters: ReportFilterField[] = [
  ...woStatusFilters,
  { key: 'customer', label: 'Customer', type: 'text', placeholder: 'Customer name or code…' },
  { key: 'project', label: 'Project', type: 'text', placeholder: 'Project # or name…' },
  {
    key: 'order_type',
    label: 'Order type',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Make to Order', label: 'Make to Order' },
      { value: 'Make to Stock', label: 'Make to Stock' },
      { value: 'Rework', label: 'Rework' },
      { value: 'Assembly', label: 'Assembly' },
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Low', label: 'Low' },
      { value: 'Normal', label: 'Normal' },
      { value: 'High', label: 'High' },
      { value: 'Urgent', label: 'Urgent' },
    ],
  },
  {
    key: 'execution_scope',
    label: 'Execution scope',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Manufacturing', label: 'Manufacturing' },
      { value: 'Fabrication', label: 'Fabrication' },
      { value: 'Repair', label: 'Repair' },
      { value: 'Repair + Manufacturing', label: 'Repair + Manufacturing' },
      { value: 'Outsource', label: 'Outsource' },
    ],
  },
];

const opsStatusFilters: ReportFilterField[] = [
  { key: 'dateRange', label: 'Date range', type: 'dateRange' },
  { key: 'search', label: 'Search', type: 'text', placeholder: 'WO #, operation, item…' },
  {
    key: 'status',
    label: 'Operation status',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Pending', label: 'Pending' },
      { value: 'In Progress', label: 'In Progress' },
      { value: 'Paused', label: 'Paused' },
      { value: 'Completed', label: 'Completed' },
      { value: 'On Hold', label: 'On Hold' },
      { value: 'Skipped', label: 'Skipped' },
      { value: 'Inspection Pending', label: 'Inspection Pending' },
      { value: 'Inspection Rejected', label: 'Inspection Rejected' },
    ],
  },
  {
    key: 'wo_status',
    label: 'WO status',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Draft', label: 'Draft' },
      { value: 'Released', label: 'Released' },
      { value: 'IIR Pending', label: 'Job Inspection Pending' },
      { value: 'In Progress', label: 'In Progress' },
      { value: 'Stage In Progress', label: 'Stage In Progress' },
      { value: 'Inspection Pending', label: 'Inspection Pending' },
      { value: 'FIR Pending', label: 'FIR Pending' },
      { value: 'Ready for Dispatch', label: 'Ready for Dispatch' },
      { value: 'Completed', label: 'Completed' },
      { value: 'Delivered', label: 'Delivered' },
      { value: 'Closed', label: 'Closed' },
      { value: 'Cancelled', label: 'Cancelled' },
    ],
  },
  { key: 'project', label: 'Project', type: 'text', placeholder: 'Project # or name…' },
  {
    key: 'execution_scope',
    label: 'Execution scope',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Manufacturing', label: 'Manufacturing' },
      { value: 'Fabrication', label: 'Fabrication' },
      { value: 'Repair', label: 'Repair' },
      { value: 'Repair + Manufacturing', label: 'Repair + Manufacturing' },
      { value: 'Outsource', label: 'Outsource' },
    ],
  },
];

const projectStatusFilters: ReportFilterField[] = [
  { key: 'search', label: 'Search', type: 'text', placeholder: 'Project #, name…' },
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Planning', label: 'Planning' },
      { value: 'In Progress', label: 'In Progress' },
      { value: 'On Hold', label: 'On Hold' },
      { value: 'Completed', label: 'Completed' },
      { value: 'Cancelled', label: 'Cancelled' },
    ],
  },
];

const hrStatusFilters: ReportFilterField[] = [
  { key: 'search', label: 'Department / employee', type: 'text' },
  {
    key: 'status',
    label: 'Employment status',
    type: 'select',
    options: [
      { value: '', label: 'All' },
      { value: 'Active', label: 'Active' },
      { value: 'On Leave', label: 'On Leave' },
      { value: 'Resigned', label: 'Resigned' },
      { value: 'Terminated', label: 'Terminated' },
    ],
  },
];

function moneyCol(key: string, title: string, width = 120): ReportColumnDef {
  return { key, title, dataIndex: key, width, align: 'right', sortable: true, format: 'money' };
}

function percentCol(key: string, title: string, width = 110): ReportColumnDef {
  return { key, title, dataIndex: key, width, align: 'right', sortable: true, format: 'percent' };
}

function textCol(key: string, title: string, width = 140, drillPath?: string): ReportColumnDef {
  return { key, title, dataIndex: key, width, sortable: true, format: 'text', drillPath };
}

function dateCol(key: string, title: string): ReportColumnDef {
  return { key, title, dataIndex: key, width: 120, sortable: true, format: 'date' };
}

function numCol(key: string, title: string, width = 100): ReportColumnDef {
  return { key, title, dataIndex: key, width, align: 'right', sortable: true, format: 'number' };
}

const recruitDateSearch: ReportFilterField[] = [
  { key: 'dateRange', label: 'Date range', type: 'dateRange' },
  { key: 'search', label: 'Search', type: 'text' },
];
const RECRUIT_REPORT_PERMS = ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage'];

/** Full FE catalog — keep ids/routes aligned with backend standard-reports.catalog.ts */
export const STANDARD_REPORTS: ReportDefinition[] = [
  {
    id: 'scm.purchase_register',
    module: 'scm',
    title: 'Purchase Register',
    description: 'Purchase orders with supplier, status, dates, and amounts',
    route: '/scm/reports/purchase-register',
    kind: 'runner',
    permissionCodes: ['procurement.reports.view', 'procurement.po.view', 'procurement.view'],
    dataSource: '/procurement/reports/purchase-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true, drillThrough: true },
    filterSchema: poStatusFilters,
    sortDefaults: { sortBy: 'po_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'PO #', 160, '/scm/procurement/purchase-orders/:id'),
      dateCol('po_date', 'PO date'),
      textCol('supplier_name', 'Supplier', 200),
      textCol('status', 'Status', 110),
      textCol('currency', 'Currency', 80),
      moneyCol('total_amount', 'Total'),
      moneyCol('base_total_amount', 'Total (base currency)', 150),
      textCol('project_name', 'Project', 160),
    ],
  },
  {
    id: 'scm.grn_register',
    module: 'scm',
    title: 'Goods Receipt Register',
    description: 'Goods receipt notes (GRN) by date, PO, supplier, and status',
    route: '/scm/reports/grn-register',
    kind: 'runner',
    permissionCodes: ['procurement.reports.view', 'procurement.grn.view', 'procurement.view'],
    dataSource: '/procurement/reports/grn-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true, drillThrough: true },
    filterSchema: grnStatusFilters,
    sortDefaults: { sortBy: 'grn_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'GRN #', 160, '/scm/procurement/grn/:id'),
      dateCol('grn_date', 'Receipt date'),
      textCol('po_number', 'PO #', 140),
      textCol('supplier_name', 'Supplier', 200),
      textCol('status', 'Status', 110),
      textCol('warehouse_name', 'Warehouse', 140),
      textCol('project_name', 'Project', 160),
    ],
  },
  {
    id: 'scm.supplier_invoice_register',
    module: 'scm',
    title: 'Supplier Invoice Register',
    description: 'Supplier invoices / purchase bills register',
    route: '/scm/reports/supplier-invoice-register',
    kind: 'runner',
    permissionCodes: ['procurement.reports.view', 'procurement.view'],
    dataSource: '/procurement/reports/supplier-invoice-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true, drillThrough: true },
    filterSchema: supplierInvoiceStatusFilters,
    sortDefaults: { sortBy: 'supplier_invoice_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Invoice #', 160, '/scm/procurement/supplier-invoices/:id'),
      dateCol('supplier_invoice_date', 'Invoice date'),
      textCol('supplier_name', 'Supplier', 200),
      textCol('status', 'Status', 110),
      moneyCol('total_amount', 'Total'),
      textCol('currency', 'Currency', 80),
      moneyCol('base_total_amount', 'Total (base currency)', 150),
    ],
  },
  {
    id: 'scm.purchase_return_register',
    module: 'scm',
    title: 'Purchase Return Register',
    description: 'Purchase returns by supplier and date',
    route: '/scm/reports/purchase-return-register',
    kind: 'runner',
    permissionCodes: ['procurement.reports.view', 'procurement.view'],
    dataSource: '/procurement/reports/purchase-return-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: purchaseReturnStatusFilters,
    sortDefaults: { sortBy: 'return_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Return #', 160, '/scm/procurement/purchase-returns/:id'),
      dateCol('return_date', 'Return date'),
      textCol('supplier_name', 'Supplier', 200),
      textCol('status', 'Status', 110),
      moneyCol('total_amount', 'Total'),
      textCol('project_name', 'Project', 160),
    ],
  },
  {
    id: 'scm.purchase_requisition_register',
    module: 'scm',
    title: 'Purchase Requisition Register',
    description: 'Purchase requests / requisitions',
    route: '/scm/reports/purchase-requisition-register',
    kind: 'runner',
    permissionCodes: ['procurement.reports.view', 'procurement.view'],
    dataSource: '/procurement/reports/purchase-requisition-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: prStatusFilters,
    sortDefaults: { sortBy: 'request_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'PR #', 160, '/scm/procurement/purchase-requests/:id'),
      dateCol('request_date', 'Request date'),
      textCol('status', 'Status', 110),
      textCol('requested_by_name', 'Requested by', 160),
      textCol('project_name', 'Project', 160),
      moneyCol('total_amount', 'Est. total'),
    ],
  },
  {
    id: 'scm.po_aging',
    module: 'scm',
    title: 'PO Aging / Open PO',
    description: 'Outstanding purchase orders by age bucket',
    route: '/scm/reports/po-aging',
    kind: 'runner',
    permissionCodes: ['procurement.reports.poAging', 'procurement.reports.view', 'procurement.view'],
    dataSource: '/procurement/reports/po-aging',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true, drillThrough: true },
    filterSchema: [
      { key: 'search', label: 'Search', type: 'text' },
      {
        key: 'age_bucket',
        label: 'Age bucket',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: '0-30', label: '0–30 days' },
          { value: '31-60', label: '31–60 days' },
          { value: '61-90', label: '61–90 days' },
          { value: '90+', label: '90+ days' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'age_days', sortDir: 'DESC' },
    columnSchema: [
      textCol('po_number', 'PO #', 160, '/scm/procurement/purchase-orders/:id'),
      dateCol('po_date', 'PO date'),
      textCol('supplier_name', 'Supplier', 200),
      { key: 'age_days', title: 'Age (days)', dataIndex: 'age_days', width: 100, align: 'right', sortable: true, format: 'number' },
      textCol('age_bucket', 'Bucket', 100),
      moneyCol('grand_total', 'Open amount'),
      textCol('currency', 'Currency', 80),
      moneyCol('base_open_amount', 'Open (base currency)', 150),
    ],
  },
  {
    id: 'scm.supplier_analysis',
    module: 'scm',
    title: 'Supplier Analysis',
    description: 'Supplier-wise purchase totals and counts',
    route: '/scm/reports/supplier-analysis',
    kind: 'runner',
    permissionCodes: ['procurement.reports.supplierAnalysis', 'procurement.reports.view', 'procurement.view'],
    dataSource: '/procurement/reports/supplier-analysis',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true },
    filterSchema: [{ key: 'dateRange', label: 'Date range', type: 'dateRange' }, { key: 'search', label: 'Supplier', type: 'text' }],
    sortDefaults: { sortBy: 'total_amount', sortDir: 'DESC' },
    columnSchema: [
      textCol('supplier_name', 'Supplier', 220),
      { key: 'po_count', title: 'PO count', dataIndex: 'po_count', width: 100, align: 'right', sortable: true, format: 'number' },
      moneyCol('total_amount', 'Total amount (base currency)', 170),
      moneyCol('avg_amount', 'Avg amount (base currency)', 170),
    ],
  },
  {
    id: 'scm.po_milestones',
    module: 'scm',
    title: 'PO Milestone Register',
    description: 'Purchase order milestone tracking',
    route: '/scm/procurement/reports/po-milestones',
    kind: 'legacy',
    permissionCodes: ['procurement.reports.view', 'procurement.po.view', 'procurement.view'],
    capabilities: {},
  },
  {
    id: 'scm.service_receipt_register',
    module: 'scm',
    title: 'Service Receipt Register',
    description: 'Service receipts against POs',
    route: '/scm/reports/service-receipt-register',
    kind: 'runner',
    permissionCodes: ['procurement.reports.view', 'procurement.view'],
    dataSource: '/procurement/reports/service-receipt-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: serviceReceiptStatusFilters,
    sortDefaults: { sortBy: 'receipt_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'SR #', 160, '/scm/procurement/service-receipts/:id'),
      dateCol('receipt_date', 'Receipt date'),
      textCol('po_number', 'PO #', 140),
      textCol('supplier_name', 'Supplier', 200),
      textCol('status', 'Status', 110),
      moneyCol('total_amount', 'Total'),
      textCol('project_name', 'Project', 160),
    ],
  },
  {
    id: 'inventory.stock_summary',
    module: 'inventory',
    title: 'Stock Summary',
    description: 'On-hand stock by item and warehouse',
    route: '/scm/inventory/stock-summary',
    kind: 'legacy',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    capabilities: {},
  },
  {
    id: 'inventory.heat_wise_stock',
    module: 'inventory',
    title: 'Heat-wise Stock',
    description: 'On-hand stock by heat number and dimension with MTC reference and traceability gaps',
    route: '/scm/inventory/heat-stock',
    kind: 'legacy',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    capabilities: {},
  },
  {
    id: 'inventory.stock_movement',
    module: 'inventory',
    title: 'Stock Movement Register',
    description: 'Inventory movements by date and type',
    route: '/scm/reports/stock-movement',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/stock-movement',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: stockMovementFilters,
    sortDefaults: { sortBy: 'movement_date', sortDir: 'DESC' },
    columnSchema: [
      dateCol('movement_date', 'Date'),
      textCol('movement_type', 'Type', 120),
      textCol('item_code', 'Item', 140),
      textCol('item_name', 'Description', 200),
      { key: 'quantity', title: 'Qty', dataIndex: 'quantity', width: 100, align: 'right', format: 'number' },
      textCol('warehouse_name', 'Warehouse', 140),
      textCol('document_number', 'Document', 140),
    ],
  },
  {
    id: 'inventory.material_request',
    module: 'inventory',
    title: 'Material Request Register',
    description: 'Material requests register',
    route: '/scm/reports/material-request-register',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/material-request-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: mrStatusFilters,
    sortDefaults: { sortBy: 'request_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'MR #', 140, '/scm/inventory/material-requests/:id'),
      dateCol('request_date', 'Date'),
      textCol('status', 'Status', 110),
      textCol('requested_by_name', 'Requested by', 160),
      textCol('work_order_number', 'Work order', 140),
      textCol('project_name', 'Project', 160),
    ],
  },
  {
    id: 'inventory.material_issue',
    module: 'inventory',
    title: 'Material Issue Register',
    description: 'Material issues register',
    route: '/scm/reports/material-issue-register',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view', 'manufacturing.reports.view'],
    dataSource: '/inventory/reports/material-issue-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: miTypeFilters,
    sortDefaults: { sortBy: 'issue_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'MI #', 140),
      dateCol('issue_date', 'Date'),
      textCol('status', 'Type', 110),
      textCol('work_order_number', 'Work order', 140),
      textCol('warehouse_name', 'Warehouse', 140),
      moneyCol('total_cost', 'Cost'),
    ],
  },
  {
    id: 'inventory.valuation',
    module: 'inventory',
    title: 'Inventory Valuation',
    description: 'Stock valuation as-of date',
    route: '/scm/reports/inventory-valuation',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/inventory-valuation',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true },
    filterSchema: [{ key: 'search', label: 'Item', type: 'text' }],
    sortDefaults: { sortBy: 'item_code', sortDir: 'ASC' },
    columnSchema: [
      textCol('item_code', 'Item', 140),
      textCol('item_name', 'Description', 200),
      textCol('warehouse_name', 'Warehouse', 140),
      { key: 'quantity', title: 'Qty', dataIndex: 'quantity', width: 100, align: 'right', format: 'number' },
      moneyCol('unit_cost', 'Unit cost'),
      moneyCol('total_value', 'Value'),
    ],
  },
  {
    id: 'inventory.gl_reconciliation',
    module: 'inventory',
    title: 'Inventory ↔ GL Reconciliation',
    description:
      'Stock valuation by inventory account vs Posted GL as-of date (investigation only; no auto-journal)',
    route: '/scm/reports/gl-reconciliation',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/gl-reconciliation',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'dateRange', label: 'As of (use end date)', type: 'dateRange' },
      { key: 'tolerance', label: 'Tolerance', type: 'number' },
      {
        key: 'view',
        label: 'View',
        type: 'select',
        options: [
          { value: 'summary', label: 'Account summary' },
          { value: 'exceptions', label: 'Exception item drill' },
        ],
      },
      { key: 'search', label: 'Search', type: 'text' },
    ],
    sortDefaults: { sortBy: 'abs_delta', sortDir: 'DESC' },
    columnSchema: [
      textCol('account_code', 'Account', 100),
      textCol('account_name', 'Account name', 200),
      textCol('item_code', 'Item', 120),
      textCol('warehouse_name', 'Warehouse', 140),
      moneyCol('inventory_value', 'Inventory value'),
      moneyCol('gl_balance', 'GL balance'),
      moneyCol('delta', 'Delta'),
      textCol('exception_flag', 'Exception', 90),
    ],
  },
  {
    id: 'inventory.project_material_reconciliation',
    module: 'inventory',
    title: 'Project Material Reconciliation',
    description: 'GRN + FOC received vs material issued vs WIP on-hand by project',
    route: '/scm/reports/project-material-reconciliation',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/project-material-reconciliation',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
      { key: 'dateRange', label: 'Movement period', type: 'dateRange' },
      { key: 'search', label: 'Item', type: 'text' },
    ],
    sortDefaults: { sortBy: 'item_code', sortDir: 'ASC' },
    columnSchema: [
      textCol('item_code', 'Item', 120),
      textCol('item_name', 'Description', 200),
      moneyCol('received_qty', 'GRN qty'),
      moneyCol('foc_qty', 'FOC qty'),
      moneyCol('issued_qty', 'Issued qty'),
      moneyCol('on_hand_wip_qty', 'WIP on hand'),
      moneyCol('theoretical_qty', 'Theoretical'),
      moneyCol('variance_qty', 'Variance'),
    ],
  },
  {
    id: 'inventory.stock_aging',
    module: 'inventory',
    title: 'Stock Aging',
    description: 'On-hand stock aging buckets by last movement date',
    route: '/scm/reports/stock-aging',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/stock-aging',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'dateRange', label: 'As of (use end date)', type: 'dateRange' },
      {
        key: 'aging_bucket',
        label: 'Bucket',
        type: 'select',
        options: [
          { value: '0-30', label: '0-30' },
          { value: '31-60', label: '31-60' },
          { value: '61-90', label: '61-90' },
          { value: '91-180', label: '91-180' },
          { value: '180+', label: '180+' },
        ],
      },
      { key: 'search', label: 'Item', type: 'text' },
    ],
    sortDefaults: { sortBy: 'age_days', sortDir: 'DESC' },
    columnSchema: [
      textCol('item_code', 'Item', 120),
      textCol('item_name', 'Description', 200),
      textCol('warehouse_name', 'Warehouse', 140),
      moneyCol('quantity', 'Qty'),
      moneyCol('inventory_value', 'Value'),
      textCol('aging_bucket', 'Bucket', 90),
      { key: 'age_days', title: 'Age days', dataIndex: 'age_days', width: 90, align: 'right', format: 'number' },
    ],
  },
  {
    id: 'inventory.abc_analysis',
    module: 'inventory',
    title: 'ABC Analysis',
    description: 'ABC classification by inventory value (A≈80% / B≈15% / C≈5%)',
    route: '/scm/reports/abc-analysis',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/abc-analysis',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      {
        key: 'abc_class',
        label: 'Class',
        type: 'select',
        options: [
          { value: 'A', label: 'A' },
          { value: 'B', label: 'B' },
          { value: 'C', label: 'C' },
        ],
      },
      { key: 'search', label: 'Item', type: 'text' },
    ],
    sortDefaults: { sortBy: 'rank', sortDir: 'ASC' },
    columnSchema: [
      textCol('abc_class', 'ABC', 70),
      textCol('item_code', 'Item', 120),
      textCol('item_name', 'Description', 200),
      moneyCol('quantity', 'Qty'),
      moneyCol('inventory_value', 'Value'),
      moneyCol('value_share_pct', 'Share %'),
      moneyCol('cumulative_pct', 'Cum %'),
    ],
  },
  {
    id: 'inventory.dead_stock',
    module: 'inventory',
    title: 'Dead Stock',
    description: 'On-hand items with no movement for N days (default 180)',
    route: '/scm/reports/dead-stock',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view'],
    dataSource: '/inventory/reports/dead-stock',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'dateRange', label: 'As of (use end date)', type: 'dateRange' },
      { key: 'inactive_days', label: 'Idle days ≥', type: 'number' },
      { key: 'search', label: 'Item', type: 'text' },
    ],
    sortDefaults: { sortBy: 'days_idle', sortDir: 'DESC' },
    columnSchema: [
      textCol('item_code', 'Item', 120),
      textCol('item_name', 'Description', 200),
      textCol('warehouse_name', 'Warehouse', 140),
      moneyCol('quantity', 'Qty'),
      moneyCol('inventory_value', 'Value'),
      { key: 'days_idle', title: 'Days idle', dataIndex: 'days_idle', width: 100, align: 'right', format: 'number' },
    ],
  },
  {
    id: 'inventory.purchase_price_variance',
    module: 'inventory',
    title: 'Purchase Price Variance (PPV)',
    description: 'PO unit price vs GRN receipt/landed rate variance',
    route: '/scm/reports/purchase-price-variance',
    kind: 'runner',
    permissionCodes: ['inventory.reports.view', 'inventory.view', 'procurement.reports.view'],
    dataSource: '/inventory/reports/purchase-price-variance',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'dateRange', label: 'GRN receipt date', type: 'dateRange' },
      {
        key: 'variance_only',
        label: 'Variance only',
        type: 'select',
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
      { key: 'search', label: 'Search', type: 'text' },
    ],
    sortDefaults: { sortBy: 'receipt_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('grn_number', 'GRN', 120),
      textCol('po_number', 'PO', 120),
      textCol('item_code', 'Item', 120),
      moneyCol('accepted_qty', 'Qty'),
      moneyCol('po_unit_price', 'PO rate'),
      moneyCol('receipt_rate', 'Receipt rate'),
      moneyCol('unit_variance', 'Unit var'),
      moneyCol('value_variance', 'Value var'),
    ],
  },
  {
    id: 'inventory.demand_planning',
    module: 'inventory',
    title: 'Demand Planning (Suggested Buy)',
    description:
      'Open MR + open SO + reorder − free − on-order; use Demand Planning page to create Draft PR',
    route: '/scm/reports/demand-planning',
    kind: 'legacy',
    permissionCodes: [
      'inventory.reports.view',
      'inventory.view',
      'procurement.pr.view',
      'procurement.pr.create',
    ],
    capabilities: {},
  },
  {
    id: 'inventory.landed_cost_revalue',
    module: 'inventory',
    title: 'Landed Cost Revalue',
    description: 'Post-GRN landed cost revaluation candidates and apply actions (INV-013)',
    route: '/scm/reports/landed-cost-revalue',
    kind: 'legacy',
    permissionCodes: [
      'inventory.reports.view',
      'inventory.view',
      'inventory.adjustment.create',
      'inventory.adjustment.post',
    ],
    capabilities: {},
  },
  {
    id: 'inventory.cspm_on_hand',
    module: 'inventory',
    title: 'Externally Owned Property On Hand',
    description: 'Externally owned stock still in custody (ownership = CUSTOMER)',
    route: '/scm/reports/cspm-on-hand',
    kind: 'runner',
    permissionCodes: ['cspm.view', 'inventory.reports.view', 'inventory.view'],
    dataSource: '/cspm/reports/on-hand',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    columnSchema: [
      textCol('customer_name', 'Customer', 180),
      textCol('item_code', 'Item', 140),
      textCol('item_name', 'Description', 200),
      textCol('warehouse_name', 'Warehouse', 140),
      { key: 'available_quantity', title: 'Available', dataIndex: 'available_quantity', width: 100, align: 'right', format: 'number' },
      textCol('uom', 'UoM', 80),
    ],
  },
  {
    id: 'inventory.cspm_awaiting_inspection',
    module: 'inventory',
    title: 'Externally Owned Property Awaiting Inspection',
    description: 'Posted EOP receipts pending inspection release',
    route: '/scm/reports/cspm-awaiting-inspection',
    kind: 'runner',
    permissionCodes: ['cspm.view', 'inventory.reports.view', 'inventory.view'],
    dataSource: '/cspm/reports/awaiting-inspection',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    columnSchema: [
      textCol('receipt_number', 'EOP #', 140, '/cspm/receipts/:id'),
      textCol('customer_name', 'Customer', 180),
      textCol('source_document_type', 'Source type', 140),
      dateCol('posted_at', 'Posted'),
    ],
  },
  {
    id: 'inventory.cspm_exceptions',
    module: 'inventory',
    title: 'Externally Owned Property Exception Register',
    description: 'Damage, loss, and disposition exceptions for externally owned property',
    route: '/scm/reports/cspm-exceptions',
    kind: 'runner',
    permissionCodes: ['cspm.view', 'inventory.reports.view', 'inventory.view'],
    dataSource: '/cspm/reports/exceptions',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    columnSchema: [
      textCol('exception_number', 'Exception #', 140),
      textCol('exception_type', 'Type', 120),
      textCol('status', 'Status', 110),
      textCol('customer_name', 'Customer', 180),
      textCol('receipt_number', 'EOP #', 140),
      { key: 'quantity', title: 'Qty', dataIndex: 'quantity', width: 90, align: 'right', format: 'number' },
      textCol('notification_ref', 'Notify ref', 160),
    ],
  },
  {
    id: 'inventory.cspm_reconciliation',
    module: 'inventory',
    title: 'Externally Owned Property Reconciliation',
    description: 'Custody reconciliation workspace and statement',
    route: '/cspm/reconciliations',
    kind: 'legacy',
    permissionCodes: ['cspm.reconcile', 'cspm.view'],
    capabilities: {},
  },
  {
    id: 'inventory.cspm_quality_hold',
    module: 'inventory',
    title: 'Externally Owned Property Quality Hold',
    description: 'Externally owned stock sitting in Quarantine warehouses',
    route: '/scm/reports/cspm-quality-hold',
    kind: 'runner',
    permissionCodes: ['cspm.view', 'inventory.reports.view', 'inventory.view'],
    dataSource: '/cspm/reports/quality-hold',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    columnSchema: [
      textCol('customer_name', 'Customer', 180),
      textCol('item_code', 'Item', 140),
      textCol('item_name', 'Description', 200),
      textCol('warehouse_name', 'Warehouse', 140),
      { key: 'available_quantity', title: 'Qty', dataIndex: 'available_quantity', width: 100, align: 'right', format: 'number' },
      textCol('uom', 'UoM', 80),
    ],
  },
  {
    id: 'sales.invoice_register',
    module: 'sales',
    title: 'Tax Invoice Register',
    description: 'Tax invoices by customer, date, and status (excludes customer advance invoices)',
    route: '/growth/reports/invoice-register',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/invoice-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true, drillThrough: true },
    filterSchema: [
      ...salesInvoiceStatusFilters,
      {
        key: 'view',
        label: 'View',
        type: 'select',
        options: [
          { value: 'detail', label: 'Invoice detail' },
          { value: 'customer_summary', label: 'Customer summary' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'invoice_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Invoice #', 160, '/growth/invoices/:id'),
      dateCol('invoice_date', 'Date'),
      textCol('customer_name', 'Customer', 200),
      textCol('customer_code', 'Customer code', 120),
      textCol('sales_order_number', 'SO #', 140),
      textCol('status', 'Status', 110),
      textCol('payment_status', 'Payment', 110),
      moneyCol('total_amount', 'Total'),
      moneyCol('balance_due', 'Balance'),
      textCol('currency', 'Currency', 80),
      moneyCol('base_total_amount', 'Total (base currency)', 150),
      moneyCol('base_balance_due', 'Balance (base currency)', 150),
      textCol('project_name', 'Project', 160),
    ],
    columnSchemaByView: {
      customer_summary: [
        textCol('customer_name', 'Customer', 200),
        textCol('customer_code', 'Code', 100),
        numCol('invoice_count', 'Invoices', 80),
        moneyCol('base_total_amount', 'Total (base)', 130),
        moneyCol('base_paid_amount', 'Paid (base)', 120),
        moneyCol('base_balance_due', 'Balance (base)', 130),
      ],
    },
  },
  {
    id: 'sales.order_register',
    module: 'sales',
    title: 'Customer Order Register',
    description: 'Customer orders register',
    route: '/growth/reports/order-register',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/order-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: salesOrderStatusFilters,
    sortDefaults: { sortBy: 'order_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'SO #', 160, '/growth/orders/:id'),
      dateCol('order_date', 'Date'),
      textCol('customer_name', 'Customer', 200),
      textCol('customer_code', 'Customer code', 120),
      textCol('customer_po_number', 'Customer PO', 140),
      textCol('status', 'Status', 110),
      moneyCol('total_amount', 'Total'),
      textCol('currency', 'Currency', 80),
      moneyCol('base_total_amount', 'Total (base currency)', 150),
      textCol('project_name', 'Project', 160),
    ],
  },
  {
    id: 'sales.quotation_register',
    module: 'sales',
    title: 'Quotation Conversion',
    description: 'Quotation conversion analytics and line-level conversion audit',
    route: '/growth/quotation-conversion-reports',
    kind: 'legacy',
    permissionCodes: ['crm.quotation.view', 'growth.view', 'growth.reports.view'],
    capabilities: {},
  },
  {
    id: 'sales.conversion_funnel',
    module: 'sales',
    title: 'Conversion Funnel',
    description: 'Lead→receipt funnel with pairwise conversion and period compare',
    route: '/growth/reports/conversion-funnel',
    kind: 'legacy',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    capabilities: {},
  },
  {
    id: 'sales.win_loss',
    module: 'sales',
    title: 'Win / Loss Analysis',
    description: 'Closed opportunities by reason, owner, and industry',
    route: '/growth/reports/win-loss',
    kind: 'legacy',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    capabilities: {},
  },
  {
    id: 'sales.opportunity_conversion',
    module: 'sales',
    title: 'Opportunity Conversion',
    description: 'Open pipeline, period conversion rates, and stale opportunities',
    route: '/growth/reports/opportunity-conversion',
    kind: 'legacy',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    capabilities: {},
  },
  {
    id: 'sales.salesperson_performance',
    module: 'sales',
    title: 'Salesperson Performance',
    description: 'Leads, opps, quotes, booked SO, and invoiced by salesperson',
    route: '/growth/reports/salesperson-performance',
    kind: 'legacy',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    capabilities: {},
  },
  {
    id: 'sales.weekly_performance',
    module: 'sales',
    title: 'Weekly Sales Performance',
    description: 'Activities, meetings, pipeline, quotations and wins by salesperson and team vs target and last week',
    route: '/growth/reports/weekly-sales-performance',
    kind: 'legacy',
    permissionCodes: ['growth.reports.view', 'growth.view', 'growth.sales_target.view', 'growth.sales_target.manage'],
    capabilities: { print: true, exportXlsx: true },
  },
  {
    id: 'sales.product_rst_mix',
    module: 'sales',
    title: 'Product / RST Mix',
    description: 'Invoiced revenue by revenue service type and item',
    route: '/growth/reports/product-rst-mix',
    kind: 'legacy',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    capabilities: {},
  },
  {
    id: 'sales.booking_billing_collections',
    module: 'sales',
    title: 'Booking / Billing / Collections',
    description: 'SO booked vs invoiced vs collected bridge',
    route: '/growth/reports/booking-billing-collections',
    kind: 'legacy',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    capabilities: {},
  },
  {
    id: 'sales.quotation_aging',
    module: 'sales',
    title: 'Quotation Aging',
    description: 'Open quotations by age bucket',
    route: '/growth/reports/quotation-aging',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/quotation-aging',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: [
      { key: 'dateRange', label: 'Date range', type: 'dateRange' },
      { key: 'search', label: 'Search', type: 'text' },
      {
        key: 'age_bucket',
        label: 'Age bucket',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: '0-7', label: '0–7 days' },
          { value: '8-30', label: '8–30 days' },
          { value: '31-60', label: '31–60 days' },
          { value: '60+', label: '60+ days' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'age_days', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Quote #', 160, '/growth/quotations/:id'),
      dateCol('quotation_date', 'Date'),
      dateCol('valid_until', 'Valid until'),
      textCol('customer_name', 'Customer', 200),
      textCol('salesperson', 'Salesperson', 160),
      textCol('status', 'Status', 110),
      { key: 'age_days', title: 'Age', dataIndex: 'age_days', width: 80, align: 'right', format: 'number' },
      textCol('age_bucket', 'Bucket', 100),
      moneyCol('total_amount', 'Total'),
      textCol('currency', 'Currency', 80),
      moneyCol('base_total_amount', 'Total (base currency)', 150),
    ],
  },
  {
    id: 'sales.opportunity_aging',
    module: 'sales',
    title: 'Opportunity Aging',
    description: 'Open opportunities by inactivity age',
    route: '/growth/reports/opportunity-aging',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/opportunity-aging',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: [
      { key: 'dateRange', label: 'Date range', type: 'dateRange' },
      { key: 'search', label: 'Search', type: 'text' },
      {
        key: 'age_bucket',
        label: 'Age bucket',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: '0-7', label: '0–7 days' },
          { value: '8-30', label: '8–30 days' },
          { value: '31-60', label: '31–60 days' },
          { value: '60+', label: '60+ days' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'age_days', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Opp #', 140, '/growth/opportunities/:id'),
      textCol('opportunity_name', 'Name', 200),
      textCol('customer_name', 'Customer', 180),
      textCol('sales_owner', 'Owner', 160),
      textCol('stage', 'Stage', 120),
      { key: 'age_days', title: 'Age', dataIndex: 'age_days', width: 80, align: 'right', format: 'number' },
      textCol('age_bucket', 'Bucket', 100),
      moneyCol('estimated_value', 'Value'),
    ],
  },
  {
    id: 'sales.credit_note_register',
    module: 'sales',
    title: 'Credit Note Register',
    description: 'Customer credit notes register',
    route: '/growth/reports/credit-note-register',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/credit-note-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: statusFilter(
      [
        { value: 'Draft', label: 'Draft' },
        { value: 'Posted', label: 'Posted' },
        { value: 'Cancelled', label: 'Cancelled' },
      ],
      'CN #, customer…'
    ),
    sortDefaults: { sortBy: 'credit_note_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'CN #', 160, '/fin/credit-notes/:id'),
      dateCol('credit_note_date', 'Date'),
      textCol('customer_name', 'Customer', 200),
      textCol('invoice_number', 'Invoice #', 140),
      textCol('status', 'Status', 110),
      moneyCol('total_amount', 'Amount'),
      textCol('reason', 'Reason', 200),
    ],
  },
  {
    id: 'sales.opportunity_list_register',
    module: 'sales',
    title: 'Opportunity Register',
    description: 'Opportunity list register',
    route: '/growth/reports/opportunity-register',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/opportunity-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: statusFilter(
      [
        { value: 'Open', label: 'Open' },
        { value: 'Won', label: 'Won' },
        { value: 'Lost', label: 'Lost' },
        { value: 'Abandoned', label: 'Abandoned' },
      ],
      'Opp #, name, customer…'
    ),
    sortDefaults: { sortBy: 'created_at', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Opp #', 140, '/growth/opportunities/:id'),
      textCol('opportunity_name', 'Name', 200),
      textCol('customer_name', 'Customer', 180),
      textCol('sales_owner', 'Owner', 160),
      textCol('stage', 'Stage', 120),
      textCol('status', 'Status', 100),
      moneyCol('estimated_value', 'Value'),
      dateCol('created_at', 'Created'),
    ],
  },
  {
    id: 'sales.opportunity_to_fulfillment',
    module: 'sales',
    title: 'Enquiry & Contract-Tender Register',
    description:
      'NTES-QSF-SAL-003 — Opportunity journey through quotation, PO / tender award, project, work order, and delivery',
    route: '/growth/reports/opportunity-to-fulfillment',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/opportunity-to-fulfillment',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: statusFilter(
      [
        { value: 'Open', label: 'Open' },
        { value: 'Won', label: 'Won' },
        { value: 'Lost', label: 'Lost' },
        { value: 'Abandoned', label: 'Abandoned' },
      ],
      'Opp #, customer, quote, PO, PR, project…'
    ),
    sortDefaults: { sortBy: 'opportunity_date', sortDir: 'DESC' },
    columnSchema: [
      dateCol('opportunity_date', 'Opportunity date'),
      textCol('document_number', 'Opportunity Ref', 140, '/growth/opportunities/:id'),
      textCol('customer_name', 'Customer', 180),
      textCol('description_scope', 'Description & scope', 260),
      textCol('quotation_number', 'Quotation / proposal No', 140),
      dateCol('quotation_submission_date', 'Quotation / proposal submission date'),
      textCol('po_awarded', 'PO or tender awarded', 100),
      textCol('production_request_reference', 'PR ref (PO waiver)', 160),
      textCol('customer_po_number', 'Customer PO No', 140),
      dateCol('customer_po_date', 'Customer PO received date'),
      textCol('project_number', 'Project Number', 140),
      textCol('revised_project_number', 'Revised Project Number', 140),
      textCol('work_order_status', 'Work Order Status', 120),
      textCol('work_order_completion_and_delivered_date', 'WO completion and delivered date', 180),
      textCol('remarks', 'Remarks', 200),
    ],
  },
  {
    id: 'sales.quotation_list_register',
    module: 'sales',
    title: 'Quotation Register',
    description: 'Quotation list register',
    route: '/growth/reports/quotation-list-register',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/quotation-list-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: statusFilter(
      [
        { value: 'Draft', label: 'Draft' },
        { value: 'Sent', label: 'Sent' },
        { value: 'Accepted', label: 'Accepted' },
        { value: 'Rejected', label: 'Rejected' },
        { value: 'Expired', label: 'Expired' },
        { value: 'Revised', label: 'Revised' },
      ],
      'Quote #, customer…'
    ),
    sortDefaults: { sortBy: 'quotation_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Quote #', 160, '/growth/quotations/:id'),
      dateCol('quotation_date', 'Date'),
      textCol('customer_name', 'Customer', 200),
      textCol('salesperson', 'Salesperson', 160),
      textCol('status', 'Status', 110),
      moneyCol('total_amount', 'Total'),
      moneyCol('margin', 'Margin'),
      percentCol('margin_pct', 'Margin %'),
      textCol('currency', 'Currency', 80),
      moneyCol('base_total_amount', 'Total (base currency)', 150),
      moneyCol('base_margin', 'Margin (base currency)', 150),
    ],
  },
  {
    id: 'sales.delivery_note_register',
    module: 'sales',
    title: 'Delivery Note Register',
    description: 'Delivery notes register',
    route: '/growth/reports/delivery-note-register',
    kind: 'runner',
    permissionCodes: ['growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/delivery-note-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: salesDnStatusFilters,
    sortDefaults: { sortBy: 'delivery_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'DN #', 160, '/growth/delivery-notes/:id'),
      dateCol('delivery_date', 'Date'),
      textCol('customer_name', 'Customer', 200),
      textCol('sales_order_number', 'SO #', 140),
      textCol('status', 'Status', 110),
      textCol('project_name', 'Project', 160),
    ],
  },
  {
    id: 'sales.customer_analysis',
    module: 'sales',
    title: 'Customer Analysis',
    description: 'Customer-wise sales totals',
    route: '/growth/reports/customer-analysis',
    kind: 'runner',
    permissionCodes: ['growth.reports.customerAnalysis', 'growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/customer-analysis',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true },
    filterSchema: [{ key: 'dateRange', label: 'Date range', type: 'dateRange' }, { key: 'search', label: 'Customer', type: 'text' }],
    sortDefaults: { sortBy: 'total_amount', sortDir: 'DESC' },
    columnSchema: [
      textCol('customer_name', 'Customer', 220),
      textCol('customer_code', 'Customer code', 120),
      { key: 'invoice_count', title: 'Invoices', dataIndex: 'invoice_count', width: 100, align: 'right', format: 'number' },
      moneyCol('total_amount', 'Total (base currency)', 150),
    ],
  },
  {
    id: 'sales.invoice_aging',
    module: 'sales',
    title: 'Invoice Aging / Receivables',
    description: 'Open tax invoices by age bucket',
    route: '/growth/reports/invoice-aging',
    kind: 'runner',
    permissionCodes: ['growth.reports.invoiceAging', 'growth.reports.view', 'growth.view'],
    dataSource: '/sales/reports/invoice-aging',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true, drillThrough: true },
    filterSchema: [
      { key: 'search', label: 'Search', type: 'text' },
      {
        key: 'age_bucket',
        label: 'Age bucket',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: '0-30', label: '0–30 days' },
          { value: '31-60', label: '31–60 days' },
          { value: '61-90', label: '61–90 days' },
          { value: '90+', label: '90+ days' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'age_days', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'Invoice #', 160, '/growth/invoices/:id'),
      dateCol('invoice_date', 'Invoice date'),
      dateCol('due_date', 'Due date'),
      textCol('customer_name', 'Customer', 200),
      { key: 'age_days', title: 'Age', dataIndex: 'age_days', width: 80, align: 'right', format: 'number' },
      textCol('age_bucket', 'Bucket', 100),
      moneyCol('open_amount', 'Open'),
      textCol('currency', 'Currency', 80),
      moneyCol('base_open_amount', 'Open (base currency)', 150),
      textCol('status', 'Status', 110),
    ],
  },
  {
    id: 'sales.tre_overhead',
    module: 'sales',
    title: 'TRE Overhead / Margin',
    description: 'Trading & rental overhead and margin reports',
    route: '/growth/reports/tre-overhead',
    kind: 'legacy',
    permissionCodes: ['growth.overhead.view', 'growth.profitability.view', 'growth.margin.view', 'growth.view'],
    capabilities: {},
  },
  {
    id: 'sales.project_costing',
    module: 'sales',
    title: 'Project Costing (Customer Order)',
    description: 'Sell side, estimate, actual, committed cost and margin variance for one customer order / project',
    route: '/growth/reports/project-costing',
    kind: 'legacy',
    permissionCodes: ['growth.profitability.view', 'growth.margin.view'],
    capabilities: {},
  },
  // Finance legacy links
  ...[
    ['fin.hub', 'Financial Reports Hub', '/fin/reports', 'Trial balance, P&L, balance sheet, cash flow'],
    ['fin.monthly_pl', 'Monthly P&L', '/fin/reports/monthly-profit-loss', 'Company profit & loss month by month, with last-year comparison and YTD'],
    ['fin.operational_profit', 'Operational profit', '/fin/reports/operational-profit', 'Month-wise profit from deliveries, customer order costing and purchases (not the GL)'],
    ['fin.ledger', 'Ledger', '/fin/reports/ledger', 'General ledger enquiry'],
    ['fin.project_ledger', 'Project Ledger (GL)', '/fin/reports/project-ledger', 'Detailed GL postings attributed to a project, by account'],
    ['fin.project_pl', 'Project P&L', '/fin/reports/project-profit-loss', 'Project profit & loss from the GL (single project or all projects)'],
    ['fin.customer_outstanding', 'Customer Outstanding', '/fin/reports/customer-outstanding', 'AR outstanding'],
    ['fin.supplier_outstanding', 'Supplier Outstanding', '/fin/reports/supplier-outstanding', 'AP outstanding'],
    ['fin.customer_statement', 'Customer Statement', '/fin/reports/customer-statement', 'Customer statement'],
    ['fin.supplier_statement', 'Supplier Statement', '/fin/reports/supplier-statement', 'Supplier statement'],
    ['fin.settlement_register', 'Settlement Register', '/fin/reports/settlement-register', 'Settlement register'],
    ['fin.payment_register', 'Payment Register', '/fin/reports/payment-register', 'Payment vouchers'],
    ['fin.receipt_register', 'Receipt Register', '/fin/reports/receipt-register', 'Receipt vouchers'],
    ['fin.direct_pi_register', 'Direct PI Register', '/fin/reports/direct-purchase-invoice-register', 'Direct PI'],
    ['fin.ic_balance', 'Intercompany Balance', '/fin/reports/intercompany-balance', 'IC balance'],
    ['fin.ic_statement', 'Intercompany Statement', '/fin/reports/intercompany-statement', 'IC statement'],
    ['fin.ic_consolidation', 'Group Consolidation', '/fin/reports/intercompany-consolidation', 'Consolidation'],
    ['fin.supplier_advance_register', 'Supplier Advances', '/fin/reports/supplier-advance-register', 'Supplier advances register, ageing and exposure'],
  ].map(
    ([id, title, route, description]) =>
      ({
        id,
        module: 'fin',
        title,
        description,
        route,
        kind: 'legacy',
        permissionCodes:
          id === 'fin.direct_pi_register'
            ? ['direct_purchase_invoice.view', 'finance.reports.view']
            : ['finance.reports.view', 'finance.view'],
        capabilities: { print: true },
      }) as ReportDefinition
  ),
  ...[
    ['fin.prepaid_register', 'Prepaid Register', '/fin/prepaid/reports/register', 'Prepaid expense contracts with original, recognised and remaining amounts'],
    ['fin.prepaid_schedule', 'Recognition Schedule', '/fin/prepaid/reports/schedule', 'Prepaid recognition lines by accounting period with planned vs posted'],
    ['fin.prepaid_movement', 'Prepaid Movement', '/fin/prepaid/reports/movement', 'Opening + new prepayments − recognition − refunds − write-offs = closing'],
    ['fin.prepaid_forecast', 'Recognition Forecast', '/fin/prepaid/reports/forecast', 'Future prepaid expense recognition by accounting period'],
  ].map(
    ([id, title, route, description]) =>
      ({
        id,
        module: 'fin',
        title,
        description,
        route,
        kind: 'legacy',
        permissionCodes: ['finance.recognition.view'],
        capabilities: { exportCsv: true, exportXlsx: true, drillThrough: true },
      }) as ReportDefinition
  ),
  {
    id: 'mfg.work_order_register',
    module: 'mfg',
    title: 'Work Order Register',
    description: 'Work orders by status and dates',
    route: '/mfg/reports/work-order-register',
    kind: 'runner',
    permissionCodes: ['manufacturing.reports.view', 'manufacturing.view'],
    dataSource: '/manufacturing/reports/work-order-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: woRegisterFilters,
    sortDefaults: { sortBy: 'order_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'WO #', 160, '/mfg/work-orders/:id'),
      dateCol('order_date', 'Order date'),
      textCol('status', 'Status', 110),
      textCol('customer_name', 'Customer', 180),
      textCol('item_name', 'Item', 180),
      { key: 'quantity', title: 'Qty', dataIndex: 'quantity', width: 90, align: 'right', format: 'number' },
      textCol('project_name', 'Project', 160),
      textCol('sales_order_number', 'SO #', 140),
      textCol('order_type', 'Order type', 120),
      textCol('priority', 'Priority', 90),
      textCol('execution_scope', 'Scope', 140),
    ],
  },
  {
    id: 'mfg.operations_status',
    module: 'mfg',
    title: 'Operations Status',
    description: 'Work-order operations / stages with status',
    route: '/mfg/reports/operations-status',
    kind: 'runner',
    permissionCodes: ['manufacturing.reports.view', 'manufacturing.view'],
    dataSource: '/manufacturing/reports/operations-status',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: opsStatusFilters,
    sortDefaults: { sortBy: 'work_order_number', sortDir: 'ASC' },
    columnSchema: [
      textCol('document_number', 'WO #', 140, '/mfg/work-orders/:id'),
      textCol('work_order_status', 'WO status', 120),
      textCol('project_name', 'Project', 150),
      textCol('item_name', 'Item', 160),
      textCol('boq_line_no', 'BOQ line', 80),
      {
        key: 'sequence_number',
        title: 'Op #',
        dataIndex: 'sequence_number',
        width: 70,
        align: 'right',
        format: 'number',
        sortable: true,
      },
      textCol('operation_name', 'Operation', 180),
      textCol('production_unit_number', 'Unit', 70),
      textCol('status', 'Op status', 130),
      textCol('inspection_status', 'Inspection', 110),
      textCol('outsourced_trace_status', 'Outsource', 120),
      dateCol('started_date', 'Started'),
      dateCol('completed_date', 'Completed'),
      textCol('execution_scope', 'Scope', 120),
    ],
  },
  {
    id: 'mfg.route_sheet_register',
    module: 'mfg',
    title: 'Route Sheet Register',
    description: 'Route sheet register',
    route: '/mfg/route-sheet-register',
    kind: 'legacy',
    permissionCodes: ['manufacturing.reports.view', 'manufacturing.view'],
    capabilities: {},
  },
  {
    id: 'mfg.outsourced_fabrication',
    module: 'mfg',
    title: 'Outsourced Fabrication',
    description: 'Outsourced fabrication reports',
    route: '/mfg/reports/outsourced-fabrication',
    kind: 'legacy',
    permissionCodes: ['manufacturing.outsourced_fabrication.reports', 'manufacturing.reports.view'],
    capabilities: {},
  },
  {
    id: 'qms.final_inspection',
    module: 'qms',
    title: 'Final Inspection Reports',
    description: 'Final inspection report list',
    route: '/qms/final-inspection-reports',
    kind: 'legacy',
    permissionCodes: ['manufacturing.view', 'quality.inspection.view'],
    capabilities: {},
  },
  {
    id: 'qms.dimensional_register',
    module: 'qms',
    title: 'Dimensional Inspection Register',
    description: 'Dimensional inspection register',
    route: '/qms/dimensional-inspection-register',
    kind: 'legacy',
    permissionCodes: ['quality.inspection.view', 'manufacturing.view'],
    capabilities: {},
  },
  {
    id: 'qms.rejection_register',
    module: 'qms',
    title: 'Rejection Register',
    description: 'Rejection / NCR register',
    route: '/platform/rejection-register',
    kind: 'legacy',
    permissionCodes: ['company.view'],
    capabilities: {},
  },
  {
    id: 'prj.financial_summary',
    module: 'prj',
    title: 'Project Financial Summary',
    description: 'Project list financial / health summary',
    route: '/prj/reports/financial-summary',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view'],
    dataSource: '/projects/reports/financial-summary',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: projectStatusFilters,
    sortDefaults: { sortBy: 'project_code', sortDir: 'ASC' },
    columnSchema: [
      textCol('project_code', 'Code', 120, '/prj/projects/:id'),
      textCol('project_name', 'Name', 220),
      textCol('customer_name', 'Customer', 180),
      textCol('status', 'Status', 110),
      moneyCol('revenue', 'Revenue'),
      moneyCol('cost', 'Cost'),
      moneyCol('margin', 'Margin'),
    ],
  },
  {
    id: 'prj.trial_balance',
    module: 'prj',
    title: 'Project Trial Balance',
    description: 'Cost Control CBS/WBS/category balances (Budget / Commitment / Actual) — not company CoA',
    route: '/prj/reports/trial-balance',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/trial-balance',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
      { key: 'dateRange', label: 'Period', type: 'dateRange' },
      {
        key: 'group_by',
        label: 'Group by',
        type: 'select',
        options: [
          { value: 'category', label: 'Category' },
          { value: 'wbs', label: 'WBS' },
          { value: 'cbs', label: 'CBS' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'account', sortDir: 'ASC' },
    columnSchema: [
      textCol('account', 'Account', 200),
      textCol('cost_category', 'Category', 110),
      moneyCol('budgeted', 'Budget'),
      moneyCol('opening_commitment', 'Open commitment'),
      moneyCol('period_commitment', 'Period commitment'),
      moneyCol('closing_commitment', 'Close commitment'),
      moneyCol('opening_actual', 'Open actual'),
      moneyCol('period_actual', 'Period actual'),
      moneyCol('closing_actual', 'Close actual'),
      moneyCol('available', 'Available'),
    ],
  },
  {
    id: 'prj.ledger',
    module: 'prj',
    title: 'Project Ledger',
    description: 'Cost Control commitment and actual event register with document refs',
    route: '/prj/reports/ledger',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/ledger',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
      { key: 'dateRange', label: 'Period', type: 'dateRange' },
      {
        key: 'mode',
        label: 'Ledger',
        type: 'select',
        options: [
          { value: 'both', label: 'Commitment + Actual' },
          { value: 'commitment', label: 'Commitment only' },
          { value: 'actual', label: 'Actual only' },
        ],
      },
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Document, source…' },
    ],
    sortDefaults: { sortBy: 'event_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('ledger_type', 'Type', 100),
      dateCol('event_date', 'Date'),
      textCol('document_number', 'Document', 140),
      textCol('source_type', 'Source', 120),
      textCol('cost_category', 'Category', 110),
      textCol('event_kind', 'Event', 120),
      moneyCol('amount', 'Amount'),
    ],
  },
  {
    id: 'prj.budget_vs_commitment',
    module: 'prj',
    title: 'Budget vs Commitment',
    description: 'Current Budget versus open commitments by WBS/CBS/category',
    route: '/prj/reports/budget-vs-commitment',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/budget-vs-commitment',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
      {
        key: 'group_by',
        label: 'Group by',
        type: 'select',
        options: [
          { value: 'category', label: 'Category' },
          { value: 'wbs', label: 'WBS' },
          { value: 'cbs', label: 'CBS' },
          { value: 'line', label: 'Budget line' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'label', sortDir: 'ASC' },
    columnSchema: [
      textCol('label', 'Line', 200),
      textCol('cost_category', 'Category', 110),
      moneyCol('budgeted', 'Budget'),
      moneyCol('committed', 'Committed'),
      moneyCol('available_to_commit', 'Available to commit'),
      moneyCol('variance', 'Variance'),
      moneyCol('utilization_pct', 'Util %'),
    ],
  },
  {
    id: 'prj.budget_vs_actual',
    module: 'prj',
    title: 'Budget vs Actual Cost',
    description: 'Current Budget versus actual cost with variance',
    route: '/prj/reports/budget-vs-actual',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/budget-vs-actual',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
      {
        key: 'group_by',
        label: 'Group by',
        type: 'select',
        options: [
          { value: 'category', label: 'Category' },
          { value: 'wbs', label: 'WBS' },
          { value: 'cbs', label: 'CBS' },
          { value: 'line', label: 'Budget line' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'label', sortDir: 'ASC' },
    columnSchema: [
      textCol('label', 'Line', 200),
      textCol('cost_category', 'Category', 110),
      moneyCol('budgeted', 'Budget'),
      moneyCol('actual', 'Actual'),
      moneyCol('variance', 'Variance'),
      moneyCol('variance_pct', 'Var %'),
      textCol('status', 'Status', 90),
    ],
  },
  {
    id: 'prj.budget_vs_commitment_vs_actual',
    module: 'prj',
    title: 'Budget vs Commitment vs Actual',
    description: 'Three-way Cost Control: Budget, Committed, Actual, Available, Forecast',
    route: '/prj/reports/budget-vs-commitment-vs-actual',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/budget-vs-commitment-vs-actual',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
      {
        key: 'group_by',
        label: 'Group by',
        type: 'select',
        options: [
          { value: 'category', label: 'Category' },
          { value: 'wbs', label: 'WBS' },
          { value: 'cbs', label: 'CBS' },
          { value: 'line', label: 'Budget line' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'label', sortDir: 'ASC' },
    columnSchema: [
      textCol('label', 'Line', 200),
      moneyCol('budgeted', 'Budget'),
      moneyCol('committed', 'Committed'),
      moneyCol('actual', 'Actual'),
      moneyCol('available', 'Available'),
      moneyCol('forecast', 'Forecast'),
      moneyCol('variance', 'Variance'),
      textCol('exceedance_flag', 'Over', 70),
    ],
  },
  {
    id: 'prj.cash_flow',
    module: 'prj',
    title: 'Project Cash Flow',
    description: 'Project AR/AP calendar (collections due vs supplier payables by month)',
    route: '/prj/reports/cash-flow',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/cash-flow',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
      { key: 'dateRange', label: 'Period', type: 'dateRange' },
    ],
    sortDefaults: { sortBy: 'month', sortDir: 'ASC' },
    columnSchema: [
      textCol('month', 'Month', 100),
      moneyCol('inflow_ar', 'AR inflow'),
      moneyCol('outflow_ap', 'AP outflow'),
      moneyCol('net', 'Net'),
      textCol('currency', 'CCY', 70),
    ],
  },
  {
    id: 'prj.cost_analysis',
    module: 'prj',
    title: 'Project Cost Analysis',
    description: 'Cost mix by Material / Labor / Subcontract / Equipment / Overhead',
    route: '/prj/reports/cost-analysis',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/cost-analysis',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
    ],
    sortDefaults: { sortBy: 'cost_category', sortDir: 'ASC' },
    columnSchema: [
      textCol('cost_category', 'Category', 120),
      moneyCol('budgeted', 'Budget'),
      moneyCol('committed', 'Committed'),
      moneyCol('actual', 'Actual'),
      moneyCol('eac', 'EAC'),
      moneyCol('pct_of_budget', '% of budget'),
      moneyCol('pct_actual_of_budget', 'Actual %'),
      textCol('status', 'Status', 90),
    ],
  },
  {
    id: 'prj.profitability',
    module: 'prj',
    title: 'Project Profitability',
    description: 'Sell vs cost margins (forecast / billed / cash) where sell-side exists',
    route: '/prj/reports/profitability',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/profitability',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
    ],
    sortDefaults: { sortBy: 'applicability', sortDir: 'ASC' },
    columnSchema: [
      textCol('applicability', 'Applicable', 160),
      moneyCol('contract_value', 'Contract'),
      moneyCol('ordered', 'Ordered'),
      moneyCol('billed', 'Billed'),
      moneyCol('collected', 'Collected'),
      moneyCol('actual_cost', 'Actual cost'),
      moneyCol('eac', 'EAC'),
      moneyCol('forecast_margin_pct', 'Forecast margin %'),
      moneyCol('billed_margin_pct', 'Billed margin %'),
      moneyCol('cash_margin_pct', 'Cash margin %'),
      textCol('overrun_flags', 'Flags', 180),
    ],
  },
  {
    id: 'prj.billing_vs_cost',
    module: 'prj',
    title: 'Billing vs Cost / WIP',
    description: 'Ordered / certified / billed vs actual / EAC and accrued unbilled',
    route: '/prj/reports/billing-vs-cost',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/billing-vs-cost',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
    ],
    sortDefaults: { sortBy: 'billed', sortDir: 'DESC' },
    columnSchema: [
      moneyCol('ordered', 'Ordered'),
      moneyCol('certified', 'Certified'),
      moneyCol('billed', 'Billed'),
      moneyCol('collected', 'Collected'),
      moneyCol('actual_cost', 'Actual cost'),
      moneyCol('eac', 'EAC'),
      moneyCol('under_over_billing', 'Bill vs cost'),
      moneyCol('accrued_unbilled_liability', 'Accrued unbilled'),
    ],
  },
  {
    id: 'prj.original_vs_current_vs_actual',
    module: 'prj',
    title: 'Original vs Current Budget vs Actual',
    description: 'Estimate cube: Original Approved vs Current Budget vs Actual + contingency',
    route: '/prj/reports/original-vs-current-vs-actual',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/original-vs-current-vs-actual',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name' },
    ],
    sortDefaults: { sortBy: 'available', sortDir: 'ASC' },
    columnSchema: [
      textCol('available', 'Cube available', 100),
      moneyCol('original_budget', 'Original budget'),
      moneyCol('current_budget', 'Current budget'),
      moneyCol('actual', 'Actual'),
      moneyCol('variance_original_vs_current', 'Orig→Curr var'),
      moneyCol('variance_current_vs_actual', 'Curr→Act var'),
      moneyCol('contingency_remaining', 'Contingency left'),
    ],
  },
  {
    id: 'prj.at_risk',
    module: 'prj',
    title: 'At-Risk / Overrun Register',
    description: 'Portfolio projects with BVA overrun, exceedances, or negative margin',
    route: '/prj/reports/at-risk',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/at-risk',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name (optional)', allowClear: true },
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Project #, name…' },
      {
        key: 'mode',
        label: 'Scope',
        type: 'select',
        options: [
          { value: '', label: 'At-risk only' },
          { value: 'all', label: 'All active projects' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'risk_level', sortDir: 'ASC' },
    columnSchema: [
      textCol('project_code', 'Code', 120, '/prj/projects/:id'),
      textCol('project_name', 'Name', 200),
      textCol('risk_level', 'Risk', 90),
      moneyCol('budgeted', 'Budget'),
      moneyCol('actual', 'Actual'),
      moneyCol('available', 'Available'),
      moneyCol('forecast_margin_pct', 'Fcst margin %'),
      textCol('overrun_flags', 'Flags', 200),
    ],
  },
  {
    id: 'prj.open_commitments',
    module: 'prj',
    title: 'Open Commitment Register',
    description: 'Active Cost Control commitment events (PO / SCA) with coding status',
    route: '/prj/reports/open-commitments',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view', 'project.cost_control.view'],
    dataSource: '/projects/reports/open-commitments',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name (optional)', allowClear: true },
      { key: 'dateRange', label: 'Period', type: 'dateRange' },
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Project, source…' },
    ],
    sortDefaults: { sortBy: 'event_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('project_code', 'Project', 120),
      textCol('document_number', 'Document', 140),
      textCol('source_type', 'Source', 120),
      textCol('cost_category', 'Category', 110),
      moneyCol('amount', 'Amount'),
      textCol('coded', 'Coded', 90),
      dateCol('event_date', 'Date'),
    ],
  },
  {
    id: 'prj.retention_vo_claim_aging',
    module: 'prj',
    title: 'Retention / VO / Claim Aging',
    description: 'Sell-side retention held, variations, and claims via project mobilization',
    route: '/prj/reports/retention-vo-claim-aging',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view'],
    dataSource: '/projects/reports/retention-vo-claim-aging',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'project_id', label: 'Project', type: 'project', placeholder: 'Search by code or name (optional)', allowClear: true },
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Document, project…' },
      { key: 'status', label: 'Status', type: 'text', placeholder: 'VO/Claim status filter' },
    ],
    sortDefaults: { sortBy: 'as_of', sortDir: 'DESC' },
    columnSchema: [
      textCol('item_type', 'Type', 100),
      textCol('project_code', 'Project', 120),
      textCol('document_number', 'Document', 140),
      textCol('contract_number', 'Contract', 120),
      textCol('status', 'Status', 100),
      moneyCol('amount', 'Amount'),
      dateCol('as_of', 'As of'),
    ],
  },
  {
    id: 'prj.health_hub',
    module: 'prj',
    title: 'Project Health Hub',
    description: 'Project reports and health',
    route: '/projects/reports',
    kind: 'legacy',
    permissionCodes: ['project.reports.view'],
    capabilities: {},
  },
  {
    id: 'epc.ipc_register',
    module: 'epc',
    title: 'IPC Register',
    description: 'Interim payment certificates register',
    route: '/epc/reports/ipc-register',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view'],
    ecsPackKey: 'epc',
    dataSource: '/epc/reports/ipc-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: dateStatusFilters,
    sortDefaults: { sortBy: 'certificate_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'IPC #', 160),
      dateCol('certificate_date', 'Date'),
      textCol('project_name', 'Project', 180),
      textCol('contract_number', 'Contract', 140),
      textCol('status', 'Status', 110),
      moneyCol('amount', 'Amount'),
    ],
  },
  {
    id: 'epc.sca_register',
    module: 'epc',
    title: 'Subcontract Agreement Register',
    description: 'SCA commercial register',
    route: '/epc/reports/sca-register',
    kind: 'runner',
    permissionCodes: ['project.reports.view', 'project.view'],
    ecsPackKey: 'epc',
    dataSource: '/epc/reports/sca-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, drillThrough: true },
    filterSchema: dateStatusFilters,
    sortDefaults: { sortBy: 'agreement_date', sortDir: 'DESC' },
    columnSchema: [
      textCol('document_number', 'SCA #', 160),
      dateCol('agreement_date', 'Date'),
      textCol('subcontractor_name', 'Subcontractor', 200),
      textCol('project_name', 'Project', 160),
      textCol('status', 'Status', 110),
      moneyCol('amount', 'Amount'),
    ],
  },
  {
    id: 'asset.hub',
    module: 'asset',
    title: 'Asset Reports Hub',
    description: 'Asset register export and report links',
    route: '/asset/reports',
    kind: 'legacy',
    permissionCodes: ['asset.view', 'asset.reports.view'],
    capabilities: { exportCsv: true },
  },
  {
    id: 'asset.movements',
    module: 'asset',
    title: 'Asset Movement Log',
    description: 'Asset issues, returns, transfers',
    route: '/asset/reports/movements',
    kind: 'legacy',
    permissionCodes: ['asset.view'],
    capabilities: { exportCsv: true },
  },
  {
    id: 'asset.location',
    module: 'asset',
    title: 'Asset Location Report',
    description: 'Assets by site/room',
    route: '/asset/reports/location',
    kind: 'legacy',
    permissionCodes: ['asset.view'],
    capabilities: { exportCsv: true },
  },
  {
    id: 'pmv.hire_register',
    module: 'pmv',
    title: 'PMV Hire Register',
    description: 'Plant & machinery hire register',
    route: '/pmv/hire-register',
    kind: 'legacy',
    permissionCodes: ['pmv.view'],
    ecsPackKey: 'pmv',
    capabilities: {},
  },
  {
    id: 'pmv.fuel_register',
    module: 'pmv',
    title: 'PMV Fuel Register',
    description: 'Fuel register',
    route: '/pmv/fuel-register',
    kind: 'legacy',
    permissionCodes: ['pmv.view'],
    ecsPackKey: 'pmv',
    capabilities: {},
  },
  {
    id: 'hr.headcount',
    module: 'hr',
    title: 'HR Headcount Summary',
    description: 'Employee headcount by department/status',
    route: '/hr/reports/headcount',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view'],
    dataSource: '/hr/reports/headcount',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true, schedule: true },
    filterSchema: hrStatusFilters,
    sortDefaults: { sortBy: 'department', sortDir: 'ASC' },
    columnSchema: [
      textCol('department', 'Department', 200),
      textCol('status', 'Status', 110),
      { key: 'headcount', title: 'Headcount', dataIndex: 'headcount', width: 110, align: 'right', format: 'number' },
    ],
  },
  {
    id: 'hr.attendance',
    module: 'hr',
    title: 'Attendance Summary',
    description: 'Attendance days and hours by employee',
    route: '/hr/reports/attendance',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.attendance.view'],
    dataSource: '/hr/reports/attendance-summary',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 120),
      textCol('employee_name', 'Name', 180),
      { key: 'present_days', title: 'Present', dataIndex: 'present_days', width: 90, align: 'right', format: 'number' },
      { key: 'absent_days', title: 'Absent', dataIndex: 'absent_days', width: 90, align: 'right', format: 'number' },
      { key: 'worked_hours', title: 'Hours', dataIndex: 'worked_hours', width: 90, align: 'right', format: 'number' },
    ],
  },
  {
    id: 'hr.leave_liability',
    module: 'hr',
    title: 'Leave Liability',
    description: 'Leave balances and accrued liability',
    route: '/hr/reports/leave-liability',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.leave.view'],
    dataSource: '/hr/reports/leave-liability',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: hrStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 120),
      textCol('leave_type', 'Leave type', 140),
      { key: 'balance', title: 'Balance', dataIndex: 'balance', width: 100, align: 'right', format: 'number' },
      moneyCol('liability_amount', 'Liability'),
    ],
  },
  {
    id: 'hr.benefit_valuation_register',
    module: 'hr',
    title: 'Employee Benefit Valuation Register',
    description: 'Entitlement vs accounting liability by employee (valuation method explicit)',
    route: '/hr/reports/benefit-valuation-register',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.accrual.view', 'hr.accrual.reconcile'],
    dataSource: '/hr/reports/benefit-valuation-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('benefit_type', 'Benefit', 90),
      textCol('employee_code', 'Employee', 110),
      textCol('full_name', 'Name', 160),
      textCol('valuation_method', 'Valuation method', 140),
      moneyCol('entitlement_amount', 'Entitlement'),
      moneyCol('accounting_liability', 'Acct liability'),
      moneyCol('period_expense', 'Period expense'),
      moneyCol('closing_liability', 'Closing'),
    ],
  },
  {
    id: 'hr.leave_liability_movement',
    module: 'hr',
    title: 'Monthly Leave Liability Movement',
    description: 'Leave liability opening / expense / settlements / closing',
    route: '/hr/reports/leave-liability-movement',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.accrual.view'],
    dataSource: '/hr/reports/leave-liability-movement',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 110),
      textCol('valuation_method', 'Method', 120),
      moneyCol('opening_liability', 'Opening'),
      moneyCol('period_expense', 'Expense'),
      moneyCol('settlements', 'Settlements'),
      moneyCol('closing_liability', 'Closing'),
      moneyCol('entitlement_amount', 'Entitlement'),
    ],
  },
  {
    id: 'hr.eosb_liability_movement',
    module: 'hr',
    title: 'Monthly EOSB Liability Movement',
    description: 'EOSB liability roll-forward (STATUTORY_UAE is not IAS 19)',
    route: '/hr/reports/eosb-liability-movement',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.eosb.manage', 'hr.accrual.view'],
    dataSource: '/hr/reports/eosb-liability-movement',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 110),
      textCol('valuation_method', 'Method', 120),
      moneyCol('opening_liability', 'Opening'),
      moneyCol('period_expense', 'Expense'),
      moneyCol('settlements', 'Settlements'),
      moneyCol('closing_liability', 'Closing'),
    ],
  },
  {
    id: 'hr.accrual_to_jv_register',
    module: 'hr',
    title: 'Accrual-to-JV Posting Register',
    description: 'Accounting lifecycle, valuation version, and Finance JV linkage',
    route: '/hr/reports/accrual-to-jv-register',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.accrual.accounting.manage'],
    dataSource: '/hr/reports/accrual-to-jv-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('accrual_kind', 'Kind', 80),
      textCol('accounting_lifecycle', 'Lifecycle', 120),
      textCol('finance_status', 'Finance', 110),
      textCol('valuation_method', 'Method', 120),
      moneyCol('period_expense_total', 'Period expense'),
      textCol('voucher_number', 'JV', 120),
      textCol('jv_status', 'JV status', 100),
    ],
  },
  {
    id: 'hr.hrms_gl_reconciliation',
    module: 'hr',
    title: 'HRMS-to-GL Reconciliation Statement',
    description: 'Reconciliation runs with unexplained variance and close-gate flag',
    route: '/hr/reports/hrms-gl-reconciliation',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.accrual.reconcile'],
    dataSource: '/hr/reports/hrms-gl-reconciliation',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('as_of_date', 'As of', 110),
      textCol('benefit_type', 'Type', 80),
      textCol('status', 'Status', 100),
      moneyCol('hrms_accounting_liability_total', 'HRMS liability'),
      moneyCol('gl_liability_total', 'GL liability'),
      moneyCol('unexplained_variance', 'Variance'),
    ],
  },
  {
    id: 'hr.accrual_exceptions_audit',
    module: 'hr',
    title: 'Accrual Exceptions and Adjustment Audit',
    description: 'Root-cause classified recon exceptions (no auto-balancing)',
    route: '/hr/reports/accrual-exceptions-audit',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.accrual.reconcile'],
    dataSource: '/hr/reports/accrual-exceptions-audit',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('as_of_date', 'As of', 110),
      textCol('code', 'Code', 140),
      textCol('root_cause', 'Root cause', 140),
      textCol('message', 'Message', 280),
      textCol('batch_id', 'Batch', 120),
    ],
  },
  {
    id: 'hr.ot',
    module: 'hr',
    title: 'Overtime Summary',
    description: 'OT requests and approved hours',
    route: '/hr/reports/ot',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.ot.manage'],
    dataSource: '/hr/reports/ot-summary',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 120),
      textCol('ot_date', 'Date', 110),
      { key: 'requested_hours', title: 'Requested', dataIndex: 'requested_hours', width: 100, align: 'right', format: 'number' },
      { key: 'approved_hours', title: 'Approved', dataIndex: 'approved_hours', width: 100, align: 'right', format: 'number' },
      textCol('status', 'Status', 110),
    ],
  },
  {
    id: 'hr.payroll_register',
    module: 'hr',
    title: 'Payroll Register',
    description: 'Payroll period register by employee',
    route: '/hr/reports/payroll-register',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.payroll.view'],
    dataSource: '/hr/reports/payroll-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 120),
      textCol('period', 'Period', 100),
      moneyCol('gross', 'Gross'),
      moneyCol('deductions', 'Deductions'),
      moneyCol('net', 'Net'),
    ],
  },
  {
    id: 'hr.loans',
    module: 'hr',
    title: 'Loan Register',
    description: 'Employee loans and outstanding balances',
    route: '/hr/reports/loans',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.loan.manage'],
    dataSource: '/hr/reports/loan-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('loan_number', 'Loan #', 120),
      textCol('employee_code', 'Employee', 120),
      moneyCol('principal_amount', 'Principal'),
      moneyCol('outstanding_amount', 'Outstanding'),
      textCol('status', 'Status', 110),
    ],
  },
  {
    id: 'hr.document_expiry',
    module: 'hr',
    title: 'Document Expiry',
    description: 'Employee documents nearing or past expiry',
    route: '/hr/reports/document-expiry',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view'],
    dataSource: '/hr/reports/document-expiry',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 120),
      textCol('document_type', 'Type', 120),
      textCol('document_label', 'Label', 180),
      textCol('expiry_date', 'Expiry', 110),
      textCol('status', 'Status', 100),
    ],
  },
  {
    id: 'hr.recruit_funnel',
    module: 'hr',
    title: 'Recruitment Funnel Conversion',
    description: 'Applications → screened → interviewed → offered → hired, per job',
    route: '/hr/reports/recruit-funnel',
    kind: 'runner',
    permissionCodes: RECRUIT_REPORT_PERMS,
    dataSource: '/hr/reports/recruit-funnel',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: recruitDateSearch,
    columnSchema: [
      textCol('job_number', 'Job #', 120),
      textCol('job_title', 'Job', 200),
      textCol('department', 'Department', 150),
      numCol('applications', 'Applied'),
      numCol('screened', 'Screened'),
      numCol('interviewed', 'Interviewed'),
      numCol('offered', 'Offered'),
      numCol('hired', 'Hired'),
      percentCol('interview_rate', 'Interview %'),
      percentCol('offer_rate', 'Offer %'),
      percentCol('hire_rate', 'Hire %'),
    ],
  },
  {
    id: 'hr.recruit_time_to_hire',
    module: 'hr',
    title: 'Time to Hire / Time to Fill',
    description: 'Days from application (hire) and from requisition (fill) for every hire',
    route: '/hr/reports/recruit-time-to-hire',
    kind: 'runner',
    permissionCodes: RECRUIT_REPORT_PERMS,
    dataSource: '/hr/reports/recruit-time-to-hire',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: recruitDateSearch,
    columnSchema: [
      textCol('candidate_name', 'Hire', 180),
      textCol('employee_number', 'Employee #', 110),
      textCol('job_title', 'Job', 180),
      textCol('department', 'Department', 140),
      textCol('source', 'Source', 110),
      textCol('requisition_number', 'Requisition', 120),
      dateCol('applied_on', 'Applied'),
      dateCol('offer_accepted_on', 'Offer accepted'),
      dateCol('hired_on', 'Hired'),
      numCol('days_to_hire', 'Days to hire'),
      numCol('days_to_fill', 'Days to fill'),
    ],
  },
  {
    id: 'hr.recruit_sources',
    module: 'hr',
    title: 'Source Effectiveness',
    description: 'Applications, interviews and hires by candidate source',
    route: '/hr/reports/recruit-sources',
    kind: 'runner',
    permissionCodes: RECRUIT_REPORT_PERMS,
    dataSource: '/hr/reports/recruit-sources',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: recruitDateSearch,
    columnSchema: [
      textCol('source', 'Source', 160),
      numCol('candidates', 'Candidates'),
      numCol('applications', 'Applications'),
      numCol('interviewed', 'Interviewed'),
      numCol('offered', 'Offered'),
      numCol('hired', 'Hired'),
      numCol('rejected', 'Rejected'),
      percentCol('hire_rate', 'Hire %'),
    ],
  },
  {
    id: 'hr.recruit_offer_acceptance',
    module: 'hr',
    title: 'Offer Acceptance Rate',
    description: 'Offers sent, accepted, declined and expired by month and department',
    route: '/hr/reports/recruit-offer-acceptance',
    kind: 'runner',
    permissionCodes: RECRUIT_REPORT_PERMS,
    dataSource: '/hr/reports/recruit-offer-acceptance',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: recruitDateSearch,
    columnSchema: [
      textCol('period', 'Month', 100),
      textCol('department', 'Department', 160),
      numCol('offers_sent', 'Sent'),
      numCol('accepted', 'Accepted'),
      numCol('declined', 'Declined'),
      numCol('expired', 'Expired'),
      numCol('withdrawn', 'Withdrawn'),
      numCol('awaiting', 'Awaiting'),
      percentCol('acceptance_rate', 'Acceptance %'),
      numCol('avg_response_days', 'Avg response (days)', 130),
    ],
  },
  {
    id: 'hr.recruit_workload',
    module: 'hr',
    title: 'Recruiter Workload',
    description: 'Open applications, overdue screening and hires per recruiter',
    route: '/hr/reports/recruit-workload',
    kind: 'runner',
    permissionCodes: RECRUIT_REPORT_PERMS,
    dataSource: '/hr/reports/recruit-workload',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: recruitDateSearch,
    columnSchema: [
      textCol('recruiter', 'Recruiter', 180),
      textCol('employee_number', 'Employee #', 110),
      numCol('open_applications', 'Open'),
      numCol('awaiting_screening', 'To screen'),
      numCol('overdue_screening', 'Overdue (3d+)', 120),
      numCol('in_interview', 'Interview'),
      numCol('at_offer', 'Offer'),
      numCol('hires', 'Hires'),
    ],
  },
  {
    id: 'hr.recruit_requisition_ageing',
    module: 'hr',
    title: 'Requisition Ageing',
    description: 'Open and pending requisitions by age, with fill progress',
    route: '/hr/reports/recruit-requisition-ageing',
    kind: 'runner',
    permissionCodes: RECRUIT_REPORT_PERMS,
    dataSource: '/hr/reports/recruit-requisition-ageing',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: statusFilter(
      [
        { value: 'PendingApproval', label: 'Pending approval' },
        { value: 'Open', label: 'Open' },
        { value: 'OnHold', label: 'On hold' },
        { value: 'Filled', label: 'Filled' },
        { value: 'Cancelled', label: 'Cancelled' },
      ],
      'Requisition #, job, recruiter…'
    ),
    columnSchema: [
      textCol('requisition_number', 'Requisition', 130),
      textCol('job_title', 'Job', 180),
      textCol('department', 'Department', 140),
      textCol('status', 'Status', 110),
      textCol('urgency', 'Urgency', 90),
      dateCol('raised_on', 'Raised'),
      dateCol('target_join_date', 'Target join'),
      numCol('age_days', 'Age (days)'),
      textCol('age_bucket', 'Bucket', 110),
      numCol('headcount', 'Headcount'),
      numCol('filled_count', 'Filled'),
      numCol('open_positions', 'Open'),
      numCol('applications', 'Applications'),
      textCol('past_target', 'Past target', 100),
      textCol('recruiter', 'Recruiter', 150),
    ],
  },
  {
    id: 'hr.recruit_onboarding',
    module: 'hr',
    title: 'Onboarding Completion',
    description: 'Task completion and overdue items per new-hire onboarding plan',
    route: '/hr/reports/recruit-onboarding',
    kind: 'runner',
    permissionCodes: [...RECRUIT_REPORT_PERMS, 'hr.onboarding.manage'],
    dataSource: '/hr/reports/recruit-onboarding',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: statusFilter(
      [
        { value: 'Open', label: 'Open' },
        { value: 'InProgress', label: 'In progress' },
        { value: 'Completed', label: 'Completed' },
      ],
      'Plan #, employee…'
    ),
    columnSchema: [
      textCol('plan_number', 'Plan #', 120),
      textCol('employee_name', 'Employee', 180),
      textCol('employee_number', 'Employee #', 110),
      textCol('department', 'Department', 140),
      dateCol('join_date', 'Join date'),
      dateCol('probation_end_date', 'Probation ends'),
      textCol('status', 'Status', 100),
      numCol('tasks', 'Tasks'),
      numCol('closed_tasks', 'Closed'),
      numCol('overdue_tasks', 'Overdue'),
      numCol('mandatory_open', 'Mandatory open', 120),
      percentCol('completion_pct', 'Complete %'),
    ],
  },
  {
    id: 'hr.eosb',
    module: 'hr',
    title: 'EOSB Provisions / Settlements',
    description: 'EOSB provision and settlement register',
    route: '/hr/reports/eosb',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.eosb.manage'],
    dataSource: '/hr/reports/eosb-provision',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee', 120),
      { key: 'service_years', title: 'Service yrs', dataIndex: 'service_years', width: 100, align: 'right', format: 'number' },
      moneyCol('provision_amount', 'Provision'),
      moneyCol('gratuity_amount', 'Gratuity'),
      textCol('status', 'Status', 110),
    ],
  },
  {
    id: 'hr.allocations',
    module: 'hr',
    title: 'Allocation Matrix',
    description: 'Employee project allocation matrix',
    route: '/hr/reports/allocations',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
    dataSource: '/hr/reports/allocation-matrix',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: [
      { key: 'dateRange', label: 'Active during', type: 'dateRange' },
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Employee, project, department, role…' },
      {
        key: 'status',
        label: 'Allocation status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'Current', label: 'Current' },
          { value: 'Scheduled', label: 'Scheduled' },
          { value: 'Expired', label: 'Expired (end date passed)' },
          { value: 'Ended', label: 'Ended' },
          { value: 'Cancelled', label: 'Cancelled' },
        ],
      },
    ],
    columnSchema: [
      textCol('employee_code', 'Employee #', 110),
      textCol('employee_name', 'Name', 170),
      textCol('department_name', 'Department', 140),
      textCol('project_code', 'Project', 120),
      textCol('project_name', 'Project name', 200),
      textCol('role_on_project', 'Role', 130),
      { key: 'allocation_pct', title: '%', dataIndex: 'allocation_pct', width: 70, align: 'right', format: 'number' },
      textCol('effective_from', 'From', 105),
      textCol('effective_to', 'To', 105),
      textCol('allocation_status', 'Status', 100),
      textCol('end_reason', 'End reason', 160),
    ],
  },
  {
    id: 'hr.project_manpower',
    module: 'hr',
    title: 'Project Manpower',
    description: 'Headcount and FTE allocated to each project on a date',
    route: '/hr/reports/project-manpower',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
    dataSource: '/hr/reports/project-manpower',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: [
      { key: 'dateRange', label: 'As of (use end date, default today)', type: 'dateRange' },
      { key: 'search', label: 'Project', type: 'text' },
    ],
    columnSchema: [
      textCol('project_code', 'Project', 130),
      textCol('project_name', 'Project name', 240),
      textCol('project_status', 'Project status', 120),
      { key: 'headcount', title: 'Headcount', dataIndex: 'headcount', width: 100, align: 'right', format: 'number' },
      { key: 'fte', title: 'FTE', dataIndex: 'fte', width: 90, align: 'right', format: 'number' },
      { key: 'avg_pct', title: 'Avg %', dataIndex: 'avg_pct', width: 80, align: 'right', format: 'number' },
      textCol('roles', 'Roles', 220),
      textCol('as_of', 'As of', 105),
    ],
  },
  {
    id: 'hr.allocation_exceptions',
    module: 'hr',
    title: 'Allocation Exceptions',
    description: 'Employees unallocated, under / over 100%, or on closed projects',
    route: '/hr/reports/allocation-exceptions',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
    dataSource: '/hr/reports/allocation-exceptions',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: [
      { key: 'dateRange', label: 'As of (use end date, default today)', type: 'dateRange' },
      { key: 'search', label: 'Employee / department', type: 'text' },
      {
        key: 'status',
        label: 'Issue',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'Unallocated', label: 'Unallocated' },
          { value: 'Under-allocated', label: 'Under-allocated' },
          { value: 'Over-allocated', label: 'Over-allocated' },
          { value: 'Allocated to closed project', label: 'Allocated to closed project' },
        ],
      },
    ],
    columnSchema: [
      textCol('employee_code', 'Employee #', 110),
      textCol('employee_name', 'Name', 170),
      textCol('department_name', 'Department', 140),
      textCol('employment_status', 'Status', 100),
      { key: 'total_pct', title: 'Allocated %', dataIndex: 'total_pct', width: 100, align: 'right', format: 'number' },
      { key: 'free_pct', title: 'Free %', dataIndex: 'free_pct', width: 80, align: 'right', format: 'number' },
      textCol('projects', 'Projects', 240),
      textCol('issue', 'Issue', 170),
    ],
  },
  {
    id: 'hr.timesheet_vs_allocation',
    module: 'hr',
    title: 'Timesheet vs Allocation',
    description: 'Project hours booked without an allocation, and allocations with no hours',
    route: '/hr/reports/timesheet-vs-allocation',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
    dataSource: '/hr/reports/timesheet-vs-allocation',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: [
      { key: 'dateRange', label: 'Period (default this month)', type: 'dateRange' },
      { key: 'search', label: 'Employee / project', type: 'text' },
      {
        key: 'status',
        label: 'Finding',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'Hours without allocation', label: 'Hours without allocation' },
          { value: 'Hours outside allocation dates', label: 'Hours outside allocation dates' },
          { value: 'Allocated, no hours booked', label: 'Allocated, no hours booked' },
          { value: 'OK', label: 'OK' },
        ],
      },
    ],
    columnSchema: [
      textCol('employee_code', 'Employee #', 110),
      textCol('employee_name', 'Name', 170),
      textCol('project_code', 'Project', 120),
      textCol('project_name', 'Project name', 200),
      { key: 'allocation_pct', title: 'Alloc %', dataIndex: 'allocation_pct', width: 80, align: 'right', format: 'number' },
      { key: 'days', title: 'Days', dataIndex: 'days', width: 70, align: 'right', format: 'number' },
      { key: 'hours', title: 'Hours', dataIndex: 'hours', width: 90, align: 'right', format: 'number' },
      { key: 'unallocated_hours', title: 'Unallocated hrs', dataIndex: 'unallocated_hours', width: 120, align: 'right', format: 'number' },
      textCol('finding', 'Finding', 200),
    ],
  },
  {
    id: 'hr.shift_roster',
    module: 'hr',
    title: 'Shift Roster',
    description: 'Shift in effect for each employee on a date, with upcoming changes and gaps',
    route: '/hr/reports/shift-roster',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.shift.manage'],
    dataSource: '/hr/reports/shift-roster',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: [
      { key: 'dateRange', label: 'As of (use end date, default today)', type: 'dateRange' },
      { key: 'search', label: 'Employee / department / location', type: 'text' },
      {
        key: 'status',
        label: 'Show',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'Assignment', label: 'Dated assignment' },
          { value: 'Default shift', label: 'Default shift' },
          { value: 'No shift', label: 'No shift' },
          { value: 'Inactive shift', label: 'Inactive shift' },
          { value: 'Overlapping assignments', label: 'Overlapping assignments' },
        ],
      },
    ],
    columnSchema: [
      textCol('employee_code', 'Employee #', 110),
      textCol('employee_name', 'Name', 170),
      textCol('department_name', 'Department', 140),
      textCol('location_code', 'Location', 100),
      textCol('shift_code', 'Shift', 90),
      textCol('timing', 'Timing', 110),
      textCol('source', 'Source', 120),
      textCol('effective_from', 'From', 105),
      textCol('effective_to', 'To', 105),
      textCol('next_change', 'Next change', 170),
      textCol('issue', 'Issue', 170),
    ],
  },
  {
    id: 'hr.asset_custody',
    module: 'hr',
    title: 'Asset Custody',
    description: 'Issued and returned employee assets',
    route: '/hr/reports/asset-custody',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.asset_custody.manage'],
    dataSource: '/hr/reports/asset-custody',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('issue_number', 'Issue #', 120),
      textCol('employee_code', 'Employee', 120),
      textCol('asset_name', 'Asset', 180),
      textCol('issue_date', 'Issued', 110),
      textCol('status', 'Status', 100),
    ],
  },
  {
    id: 'hr.late_register',
    module: 'hr',
    title: 'Late Register',
    description: 'Attendance days with late minutes',
    route: '/hr/reports/late-register',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.attendance.view'],
    dataSource: '/hr/reports/late-register',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_number', 'Employee #', 120),
      textCol('full_name', 'Name', 160),
      textCol('attendance_date', 'Date', 110),
      textCol('check_in', 'Check-in', 90),
      { key: 'late_minutes', title: 'Late (min)', dataIndex: 'late_minutes', width: 100, align: 'right', format: 'number' },
      textCol('status', 'Status', 100),
    ],
  },
  {
    id: 'hr.project_labour',
    module: 'hr',
    title: 'Project Labour',
    description: 'Project hours and payroll cost by project',
    route: '/hr/reports/project-labour',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view'],
    dataSource: '/hr/reports/project-labour',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('project_code', 'Project', 140),
      { key: 'hours', title: 'Hours', dataIndex: 'hours', width: 100, align: 'right', format: 'number' },
      { key: 'amount', title: 'Amount', dataIndex: 'amount', width: 120, align: 'right', format: 'money' },
      { key: 'employee_count', title: 'Employees', dataIndex: 'employee_count', width: 110, align: 'right', format: 'number' },
    ],
  },
  {
    id: 'hr.eosb_settlement',
    module: 'hr',
    title: 'EOSB Settlement Register',
    description: 'End-of-service settlements',
    route: '/hr/reports/eosb-settlement',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.eosb.manage'],
    dataSource: '/hr/reports/eosb-settlement',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('settlement_number', 'Settlement #', 130),
      textCol('employee_number', 'Employee #', 120),
      textCol('full_name', 'Name', 160),
      textCol('exit_type', 'Exit type', 120),
      textCol('last_working_date', 'Last day', 110),
      { key: 'net_payable', title: 'Net payable', dataIndex: 'net_payable', width: 120, align: 'right', format: 'money' },
      textCol('status', 'Status', 100),
    ],
  },
  {
    id: 'hr.eosb_entitlement_statement',
    module: 'hr',
    title: 'EOSB Entitlement Statement',
    description:
      'Employee-wise statutory entitlement and STATUTORY_UAE accounting liability mirror (not IAS 19)',
    route: '/hr/reports/eosb-entitlement-statement',
    kind: 'runner',
    permissionCodes: ['hr.reports.view', 'hr.view', 'hr.eosb.manage'],
    dataSource: '/hr/reports/eosb-entitlement-statement',
    capabilities: { print: true, exportCsv: true, exportXlsx: true },
    filterSchema: dateStatusFilters,
    columnSchema: [
      textCol('employee_code', 'Employee #', 110),
      textCol('full_name', 'Name', 160),
      textCol('date_of_joining', 'Join date', 110),
      { key: 'service_years', title: 'Service yrs', dataIndex: 'service_years', width: 100, align: 'right', format: 'number' },
      moneyCol('current_basic', 'Current basic'),
      moneyCol('statutory_entitlement', 'Statutory entitlement'),
      moneyCol('accounting_liability', 'Accounting liability'),
      textCol('formula_key', 'Formula', 140),
      textCol('valuation_note', 'Note', 200),
    ],
  },
  {
    id: 'grc.moc_reports',
    module: 'grc',
    title: 'MOC Reports',
    description: 'Management of change reports',
    route: '/grc/moc/reports',
    kind: 'legacy',
    permissionCodes: ['moc.view', 'company.view'],
    capabilities: { exportCsv: true },
  },
  {
    id: 'analytics.sales',
    module: 'analytics',
    title: 'Sales Analytics',
    description: 'Sales analytics dashboard',
    route: '/analytics/sales',
    kind: 'dashboard',
    permissionCodes: ['growth.reports.view', 'growth.dashboard.view'],
    capabilities: {},
  },
  {
    id: 'analytics.scm',
    module: 'analytics',
    title: 'SCM Analytics',
    description: 'Supply chain analytics dashboard',
    route: '/analytics/scm',
    kind: 'dashboard',
    permissionCodes: ['procurement.reports.view'],
    capabilities: {},
  },
  {
    id: 'analytics.mfg',
    module: 'analytics',
    title: 'Manufacturing Analytics',
    description: 'Manufacturing analytics dashboard',
    route: '/analytics/mfg',
    kind: 'dashboard',
    permissionCodes: ['manufacturing.reports.view'],
    capabilities: {},
  },
  {
    id: 'education.strength',
    module: 'education',
    title: 'Section strength',
    description: 'Section capacity vs enrolled students by session and grade',
    route: '/education/reports/strength',
    kind: 'runner',
    permissionCodes: ['education.reports.view', 'education.academic.view', 'education.admin'],
    ecsPackKey: 'education_core',
    dataSource: '/education/reports/strength',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Section, grade, session…' },
      {
        key: 'status',
        label: 'Session status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'draft', label: 'Draft' },
          { value: 'active', label: 'Active' },
          { value: 'closed', label: 'Closed' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'section', sortDir: 'ASC' },
    columnSchema: [
      textCol('session', 'Session', 120),
      textCol('grade', 'Grade', 90),
      textCol('section', 'Section', 120),
      { key: 'capacity', title: 'Capacity', dataIndex: 'capacity', width: 100, align: 'right', format: 'number' },
      { key: 'enrolled', title: 'Enrolled', dataIndex: 'enrolled', width: 100, align: 'right', format: 'number' },
      {
        key: 'seats_available',
        title: 'Seats left',
        dataIndex: 'seats_available',
        width: 100,
        align: 'right',
        format: 'number',
      },
      textCol('session_status', 'Session status', 120),
    ],
  },
  {
    id: 'education.attendance',
    module: 'education',
    title: 'Attendance summary',
    description: 'Present / absent / late / excused marks by section for submitted registers',
    route: '/education/reports/attendance',
    kind: 'runner',
    permissionCodes: [
      'education.reports.view',
      'education.attendance.view',
      'education.attendance.mark',
      'education.admin',
    ],
    ecsPackKey: 'education_core',
    dataSource: '/education/reports/attendance-summary',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'dateRange', label: 'Date range', type: 'dateRange' },
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Section, grade…' },
    ],
    sortDefaults: { sortBy: 'absent_count', sortDir: 'DESC' },
    columnSchema: [
      textCol('grade', 'Grade', 90),
      textCol('section', 'Section', 140),
      {
        key: 'register_count',
        title: 'Registers',
        dataIndex: 'register_count',
        width: 100,
        align: 'right',
        format: 'number',
      },
      {
        key: 'present_count',
        title: 'Present',
        dataIndex: 'present_count',
        width: 90,
        align: 'right',
        format: 'number',
      },
      {
        key: 'absent_count',
        title: 'Absent',
        dataIndex: 'absent_count',
        width: 90,
        align: 'right',
        format: 'number',
      },
      { key: 'late_count', title: 'Late', dataIndex: 'late_count', width: 80, align: 'right', format: 'number' },
      {
        key: 'excused_count',
        title: 'Excused',
        dataIndex: 'excused_count',
        width: 90,
        align: 'right',
        format: 'number',
      },
      {
        key: 'present_rate',
        title: 'Present %',
        dataIndex: 'present_rate',
        width: 100,
        align: 'right',
        format: 'number',
      },
    ],
  },
  {
    id: 'education.fee_aging',
    module: 'education',
    title: 'Fee aging / outstanding',
    description: 'Unpaid fee demands by age bucket (collection desk export; not a Finance SOA)',
    route: '/education/reports/fee-aging',
    kind: 'runner',
    permissionCodes: [
      'education.reports.view',
      'education.fee.view',
      'education.fee.collect',
      'education.admin',
    ],
    ecsPackKey: 'education_core',
    dataSource: '/education/reports/fee-aging',
    capabilities: { print: true, exportCsv: true, exportXlsx: true, savedViews: true },
    filterSchema: [
      { key: 'search', label: 'Search', type: 'text', placeholder: 'Admission, student, fee head…' },
      {
        key: 'status',
        label: 'Demand status',
        type: 'select',
        options: [
          { value: '', label: 'All open/invoiced' },
          { value: 'open', label: 'Open' },
          { value: 'invoiced', label: 'Invoiced' },
        ],
      },
      {
        key: 'age_bucket',
        label: 'Age bucket',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: '0-30', label: '0–30 days' },
          { value: '31-60', label: '31–60 days' },
          { value: '61-90', label: '61–90 days' },
          { value: '90+', label: '90+ days' },
        ],
      },
    ],
    sortDefaults: { sortBy: 'age_days', sortDir: 'DESC' },
    columnSchema: [
      textCol('admission_no', 'Admission', 110),
      textCol('student', 'Student', 160),
      textCol('fee_head', 'Fee head', 110),
      textCol('period_key', 'Period', 100),
      textCol('status', 'Status', 100),
      moneyCol('amount', 'Amount', 110),
      textCol('currency', 'Currency', 80),
      { key: 'age_days', title: 'Age', dataIndex: 'age_days', width: 80, align: 'right', format: 'number' },
      textCol('age_bucket', 'Bucket', 90),
      textCol('collection_hold', 'Hold', 80),
    ],
  },
];

export function getReportById(id: string): ReportDefinition | undefined {
  return STANDARD_REPORTS.find((r) => r.id === id);
}

export function getReportByRoute(route: string): ReportDefinition | undefined {
  return STANDARD_REPORTS.find((r) => r.route === route);
}

export function getReportsByModule(module: ReportDefinition['module']): ReportDefinition[] {
  return STANDARD_REPORTS.filter((r) => r.module === module);
}

export function filterReportsByPermissions(
  reports: ReportDefinition[],
  permissions: string[] | undefined,
  isSuperAdmin?: boolean
): ReportDefinition[] {
  if (isSuperAdmin) return reports;
  const set = new Set(permissions || []);
  return reports.filter((r) => !r.permissionCodes.length || r.permissionCodes.some((c) => set.has(c)));
}
